import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import globalStyles from './nuvance-video.global.pcss?inline';
import innerStyles from './nuvance-video.inner.pcss?inline';

/**
 * The Nuvance Video
 * @element nuvance-video
 */
@customElement('nuvance-video')
export class NuvanceVideo extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({ reflect: true, attribute: 'video-url' })
  videoUrl: string | null = null;

  @property({ reflect: true, attribute: 'video-type' })
  videoType: 'youtube' | 'freshpaint' | null = null;

  /**
   * Id of the YouTube video.
   */
  @state()
  private youtubeVideoId: string | null = null;

  render(): TemplateResult {
    let title: string | null = null;
    let srcUrl: string | null = null;
    let referrerPolicy: string | null = null;

    if (this.videoUrl && this.videoType === 'youtube') {
      try {
        // Verify it is a valid URL and set up for getting the video ID.
        const url = new URL(this.videoUrl);

        const params = new URLSearchParams(url.search);
        // YouTube video URL.
        if (this.videoUrl.includes('youtube.com'))
          this.youtubeVideoId = params.get('v');
        // YouTube video share URL.
        if (this.videoUrl.includes('youtu.be'))
          this.youtubeVideoId = url.pathname.substring(1);

        title = 'YouTube video player';
        if (this.youtubeVideoId)
          srcUrl = `https://www.youtube-nocookie.com/embed/${this.youtubeVideoId}?rel=0`;
        // Youtube docs and embed code uses the Referrer Policy attribute.
        referrerPolicy = 'referrerpolicy="strict-origin-when-cross-origin"';
      } catch {
        console.error('Error getting the YouTube video URL');
      }
    } else if (this.videoUrl && this.videoType === 'freshpaint') {
      try {
        // Verify it is a valid URL (not just a string).
        const url = new URL(this.videoUrl);

        title = 'Freshpaint video player';

        // Quick validation that it is the type of URL we are expecting.
        if (url.pathname.substring(1)) srcUrl = url.href;
      } catch {
        console.error('Error getting the Freshpaint video URL');
      }
    }

    return html` <div class="video-container">
      ${srcUrl
        ? html` <iframe
            width="100%"
            height="100%"
            src="${srcUrl}"
            title="${title}"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ${referrerPolicy}
            allowfullscreen
          >
          </iframe>`
        : html`<p>
            Unable to load video. Please check the embedded URL and try again.
          </p>`}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-video': NuvanceVideo;
  }
}
